import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { Home, Star } from "@mui/icons-material";
import { Box, ListItem, ListItemIcon, ListItemText, Paper, Stack } from "@mui/material";
import { splitFavoriteDescription } from "../../common/utilities/SplitFavoriteDescription";
import ActionMenu from "../action-menu/ActionMenu";
import ListDragHandle from "../list-drag-handle/ListDragHandle";
import { UserFavoritesManagerFavoriteFieldType } from "./UserFavoritesManagerFieldTypes";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useMemo } from "react";

export type UserFavoriteListItemProps = {
    // ControlledField<UserFavoritesManagerFavoriteFieldType>
    //item: ControlledField<UserFavoritesManagerFavoriteFieldType>;//UserFavoritesManagerFavoriteFieldType,
    item: UserFavoritesManagerFavoriteFieldType;
    elevation?: number;
    onSetAsHome?: (item: UserFavoritesManagerFavoriteFieldType) => void;
    onRemove?: (item: UserFavoritesManagerFavoriteFieldType) => void;
    onRename?: (item: UserFavoritesManagerFavoriteFieldType) => void;
    onAssignHomePage?: (item: UserFavoritesManagerFavoriteFieldType) => void;
    onShare?: (item: UserFavoritesManagerFavoriteFieldType) => void;
};

export function UserFavoritesManagerListItem({
    item,
    elevation,
    onSetAsHome,
    onRemove,
    onRename,
    onAssignHomePage,
    onShare
}: UserFavoriteListItemProps) {

    const [userState] = useUserContext();
    const { t } = useTranslation();

    const userIsElynxAdmin = userState?.user?.isElynxAdmin ?? false;

    const {
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
        setNodeRef,
        setActivatorNodeRef
    } = useSortable({ id: item.id });

    const { viewDescription, objectDescription } = splitFavoriteDescription(item.description);
    const actionItems = useMemo(() => {
        
        let actions = [{
                label: "Set as Home Page",
                disabled: item.isHomeUrl,
                onClick: () => onSetAsHome && onSetAsHome(item)
            }, {
                label: "Remove",
                onClick: () => onRemove && onRemove(item)
            }, {
                label: "Rename",
                onClick: () => onRename && onRename(item)
            }
        ];

        if (userIsElynxAdmin) {

            actions = actions.concat([{
                    label: "Assign Home Page",
                    onClick: () => onAssignHomePage && onAssignHomePage(item)
                }, {
                    label: "Share Favorite",
                    onClick: () => onShare && onShare(item)
                }
            ]);
        }

        return actions;
    }, [
        userIsElynxAdmin
    ]);

    return (
        <ListItem
            ref={setNodeRef}
            disablePadding
            style={{
                position: "relative",
                transform: CSS.Transform.toString(transform),
                transition
            }}
            sx={{
                display: "flex",
                flexDirection: "row",
                flex: "1 1 auto",
                opacity: isDragging ? theme => theme.palette.action.disabledOpacity : 1,
                marginBottom: 1
            }}
        >
            <Paper
                elevation={elevation}
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flex: "1 1 auto",
                    alignItems: "center",
                    width: 1
                }}>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "flex-start",
                        flex: "1 1 auto"
                    }}>
                    <ListItemIcon
                        sx={{
                            margin: 1,
                            minWidth: "inherit",
                            flex: "0 0 auto"
                        }}
                    >
                        {item.isHomeUrl
                            ? <Home />
                            : <Star />
                        }
                    </ListItemIcon>

                    <ListItemText
                        sx={{
                            marginTop: 1,
                            marginBottom: 1,
                            flex: "1 1 auto",
                        }}
                        primary={
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    overflowWrap: "anywhere"
                                }}>
                                {viewDescription}
                            </Box>
                        }
                        secondary={
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    overflowWrap: "anywhere"
                                }}>
                                {objectDescription}
                            </Box>
                        }
                    />
                </Stack>

                <Stack
                    direction="row"
                    spacing={-1}
                    sx={{
                        alignItems: "center",
                        flex: "0 0 auto"
                    }}>
                    <ActionMenu toolTip={t("moreOptions").toString()} actionItems={actionItems} />

                    <ListDragHandle
                        ref={setActivatorNodeRef}
                        {...listeners}
                        {...attributes}
                    />
                </Stack>
            </Paper>
        </ListItem >
    );
}
